/**
 * Admin — Partner Territory & Business Details modal (Slice A)
 * ============================================================
 *
 * Reached from any partner card in /admin/partners. Owns the
 * admin-controlled territory fields (postcodes, suburbs, state,
 * region, mail-out, commission, ABN) with:
 *   • bulk-paste postcode input (comma / space / newline separated)
 *   • four-digit validation + de-dupe + editable tag chips
 *   • overlap preflight with soft-warn banner
 *   • explicit "I confirm this overlap is intentional" checkbox
 *   • territory_status lifecycle: draft / confirmed / review_required
 *   • audit-trail view for saved overlaps
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  X, MapPin, AlertTriangle, Info, Loader2, Plus, Trash2, ShieldCheck, Home,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import AddressStructuredForm from "@/components/partner/AddressStructuredForm";

const AU_STATES = ["ACT", "NSW", "NT", "QLD", "SA", "TAS", "VIC", "WA"];
const TERRITORY_STATUS = [
  { key: "draft",           label: "Draft",             tone: "bg-slate-100 text-slate-700" },
  { key: "confirmed",       label: "Confirmed",         tone: "bg-emerald-100 text-emerald-800" },
  { key: "review_required", label: "Review required",   tone: "bg-amber-100 text-amber-800" },
];

export default function AdminPartnerTerritoryModal({ partner, onClose, onSaved }) {
  const [postcodesRaw, setPostcodesRaw] = useState("");
  const [postcodes, setPostcodes] = useState(partner?.service_postcodes || []);
  const [suburbs, setSuburbs] = useState((partner?.service_suburbs || []).join(", "));
  const [state, setState] = useState(partner?.service_state || "");
  const [franchiseRegion, setFranchiseRegion] = useState(partner?.franchise_region || "");
  const [mailOut, setMailOut] = useState(!!partner?.mail_out_capable);
  const [commission, setCommission] = useState(partner?.commission_rate ?? "");
  const [abn, setAbn] = useState(partner?.abn || "");
  // Feb 2026 — structured Google-Places-backed business address.
  // Falls back to a blank shell if the partner only has the legacy
  // free-text `physical_address` string — we DO NOT auto-migrate; the
  // legacy value is shown read-only below so the admin can eyeball it
  // and choose to re-enter through the new autocomplete when ready.
  const legacyAddress = partner?.physical_address || "";
  const [physicalAddress, setPhysicalAddress] = useState(
    partner?.physical_address_structured || {
      line1: "", line2: "", suburb: "", state: "", postcode: "",
      country: "AU", formatted: "",
    },
  );
  const [territoryStatus, setTerritoryStatus] = useState(partner?.territory_status || "draft");
  const [overlapConfirmed, setOverlapConfirmed] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [preflighting, setPreflighting] = useState(false);
  const [audit, setAudit] = useState([]);

  // Load audit history on open.
  useEffect(() => {
    if (!partner?.id) return;
    api.get(`/admin/partners/${partner.id}/territory/audit`)
      .then(({ data }) => setAudit(data || []))
      .catch(() => {});
  }, [partner?.id]);

  // Preflight overlap check whenever the postcode set stabilises.
  useEffect(() => {
    if (!postcodes.length) { setConflicts([]); return; }
    let cancelled = false;
    setPreflighting(true);
    api.post("/admin/partners/territory/preflight", {
      service_postcodes: postcodes,
      partner_id: partner?.id,
    })
      .then(({ data }) => { if (!cancelled) setConflicts(data.conflicts || []); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setPreflighting(false); });
    return () => { cancelled = true; };
  }, [postcodes, partner?.id]);

  function addBulkPostcodes() {
    if (!postcodesRaw.trim()) return;
    // Parse locally so the admin gets instant feedback on invalid codes
    // — server-side validation still runs on save.
    const tokens = postcodesRaw
      .split(/[\s,;]+/).map((t) => t.trim()).filter(Boolean);
    const valid = [];
    const invalid = [];
    tokens.forEach((t) => (/^\d{4}$/.test(t) ? valid.push(t) : invalid.push(t)));
    const merged = Array.from(new Set([...postcodes, ...valid])).sort();
    setPostcodes(merged);
    setPostcodesRaw("");
    if (invalid.length) toast.error(`Ignored invalid: ${invalid.slice(0, 5).join(", ")}`);
  }

  function removePostcode(pc) {
    setPostcodes((prev) => prev.filter((p) => p !== pc));
  }

  const hasConflicts = conflicts.length > 0;
  const canSave = !saving && (!hasConflicts || overlapConfirmed);

  async function save() {
    if (!canSave) return;
    setSaving(true);
    try {
      // Backend exposes PUT (not PATCH) at /admin/partners/{id} — mismatch
      // was causing a 405 Method Not Allowed on Save territory.
      const { data } = await api.put(`/admin/partners/${partner.id}`, {
        email: partner.email,   // required by PartnerPayload
        partner_type: partner.partner_type,
        service_postcodes: postcodes,
        service_suburbs: suburbs,
        service_state: state || null,
        franchise_region: franchiseRegion || null,
        mail_out_capable: mailOut,
        commission_rate: commission === "" ? null : Number(commission),
        abn: abn || null,
        // Feb 2026 — send structured address. Backend also derives the
        // legacy free-text `physical_address` string from the structured
        // `formatted` value, so we don't need to send both. When the
        // admin has cleared every field we pass `null` so the backend
        // wipes the structured payload cleanly (legacy field is kept
        // untouched — see partner_territory_service.apply_territory_update).
        physical_address_structured:
          (physicalAddress && (physicalAddress.line1 || physicalAddress.suburb || physicalAddress.postcode))
            ? physicalAddress
            : null,
        territory_status: territoryStatus,
        overlap_confirmed: overlapConfirmed,
      });
      toast.success(
        conflicts.length
          ? "Saved with confirmed overlap — status set to review required."
          : "Territory saved.",
      );
      onSaved?.(data);
      onClose?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === "postcode_overlap_requires_confirmation") {
        setConflicts(detail.conflicts || []);
        toast.error("Overlap detected — please tick the confirmation to save.");
      } else {
        toast.error(formatApiError(e));
      }
    } finally { setSaving(false); }
  }

  const currentStatusChip = useMemo(
    () => TERRITORY_STATUS.find((t) => t.key === territoryStatus) || TERRITORY_STATUS[0],
    [territoryStatus],
  );

  return (
    <div className="fixed inset-0 z-40 flex" data-testid="admin-partner-territory-modal">
      <div className="absolute inset-0 bg-slate-900/50" onClick={onClose} />
      <div className="relative ml-auto w-full max-w-2xl h-full bg-white shadow-2xl overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500">Territory & Business</div>
            <h2 className="text-lg font-semibold text-slate-900">
              {partner?.business_name || partner?.email}
            </h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Territory status */}
          <Section title="Territory status" icon={ShieldCheck}>
            <div className="flex items-center gap-3">
              <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${currentStatusChip.tone}`}>
                {currentStatusChip.label}
              </span>
              <select
                value={territoryStatus}
                onChange={(e) => setTerritoryStatus(e.target.value)}
                className="text-sm px-3 py-2 rounded-lg border border-slate-300"
                data-testid="territory-status-select"
              >
                {TERRITORY_STATUS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
          </Section>

          {/* Franchise region + state + partner_type reminder */}
          <Section title="Location">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Franchise / region">
                <input
                  type="text" value={franchiseRegion}
                  onChange={(e) => setFranchiseRegion(e.target.value)}
                  placeholder="e.g. Noosa, Toowoomba"
                  data-testid="territory-franchise-region"
                  className="w-full text-sm px-3 py-2 rounded-lg border border-slate-300"
                />
              </Field>
              <Field label="Service state">
                <select
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  data-testid="territory-state"
                  className="w-full text-sm px-3 py-2 rounded-lg border border-slate-300 bg-white"
                >
                  <option value="">—</option>
                  {AU_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
            </div>
          </Section>

          {/* Service postcodes — bulk-paste + tag chips + overlap warning */}
          <Section title="Service postcodes" icon={MapPin}>
            <div className="text-xs text-slate-500 mb-2">
              Paste one or many. Separate with commas, spaces, semicolons, or new lines. Only 4-digit AU postcodes are kept.
            </div>
            <div className="flex gap-2">
              <textarea
                rows={2}
                value={postcodesRaw}
                onChange={(e) => setPostcodesRaw(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); addBulkPostcodes(); } }}
                data-testid="territory-postcodes-input"
                placeholder="4566, 4567&#10;4568 4569"
                className="flex-1 text-sm px-3 py-2 rounded-lg border border-slate-300 font-mono resize-y"
              />
              <button
                type="button" onClick={addBulkPostcodes}
                data-testid="territory-postcodes-add"
                className="px-3 py-2 rounded-lg bg-slate-900 text-white text-sm inline-flex items-center gap-1 self-start"
              >
                <Plus className="h-4 w-4" /> Add
              </button>
            </div>
            {postcodes.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5" data-testid="territory-postcodes-tags">
                {postcodes.map((pc) => (
                  <span key={pc} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-800">
                    {pc}
                    <button onClick={() => removePostcode(pc)} className="text-slate-400 hover:text-rose-600" aria-label={`remove ${pc}`}>
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            {preflighting && (
              <div className="mt-2 text-xs text-slate-500 flex items-center gap-1">
                <Loader2 className="h-3 w-3 animate-spin" /> Checking for overlaps…
              </div>
            )}
            {hasConflicts && (
              <div
                data-testid="territory-overlap-warning"
                className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm"
              >
                <div className="flex items-start gap-2 text-amber-900">
                  <AlertTriangle className="h-4 w-4 flex-none mt-0.5" />
                  <div className="flex-1">
                    <b>Postcode overlap detected.</b>
                    <ul className="mt-1 space-y-1 text-xs">
                      {conflicts.map((c) => (
                        <li key={c.partner_id}>
                          <b>{c.business_name}</b> ({c.partner_type || "unknown type"}) already holds:
                          <span className="ml-1 font-mono">{c.overlapping_postcodes.join(", ")}</span>
                        </li>
                      ))}
                    </ul>
                    <label className="mt-2.5 inline-flex items-start gap-2 text-xs cursor-pointer">
                      <input
                        type="checkbox" checked={overlapConfirmed}
                        onChange={(e) => setOverlapConfirmed(e.target.checked)}
                        data-testid="territory-overlap-confirm"
                        className="mt-0.5 h-3.5 w-3.5"
                      />
                      <span><b>I confirm this postcode overlap is intentional.</b> Saving will set territory status to <i>Review required</i> and write an audit event.</span>
                    </label>
                  </div>
                </div>
              </div>
            )}
          </Section>

          {/* Service suburbs */}
          <Section title="Service suburbs">
            <textarea
              rows={2}
              value={suburbs}
              onChange={(e) => setSuburbs(e.target.value)}
              placeholder="Noosaville, Tewantin, Cooroy"
              data-testid="territory-suburbs"
              className="w-full text-sm px-3 py-2 rounded-lg border border-slate-300 resize-y"
            />
            <div className="text-[11px] text-slate-500 mt-1">Comma-separated. Duplicates removed automatically.</div>
          </Section>

          {/* Business fields */}
          <Section title="Business details (admin-supplied)">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Commission rate (%)">
                <input
                  type="number" min="0" max="100" step="0.1"
                  value={commission}
                  onChange={(e) => setCommission(e.target.value)}
                  data-testid="territory-commission"
                  className="w-full text-sm px-3 py-2 rounded-lg border border-slate-300"
                />
                {/* Oct 2026 — this field is not wired to any payout engine
                    outside the pws_franchisee royalty calculator. Shown for
                    every other partner type so admins don't assume filling
                    it in triggers a real payment. */}
                {partner?.partner_type !== "pws_franchisee" && (
                  <p
                    data-testid="territory-commission-disclaimer"
                    className="mt-1 text-[11px] text-amber-700 leading-snug"
                  >
                    Informational only — recorded for reference, does not trigger automatic commission payouts.
                  </p>
                )}
              </Field>
              <Field label="ABN (if known)">
                <input
                  type="text" value={abn} maxLength={20}
                  onChange={(e) => setAbn(e.target.value)}
                  placeholder="11 digits, no spaces"
                  data-testid="territory-abn"
                  className="w-full text-sm px-3 py-2 rounded-lg border border-slate-300"
                />
              </Field>
            </div>
            <div className="mt-3">
              <Field label="Physical business address (if known)">
                <AddressStructuredForm
                  value={physicalAddress}
                  onChange={setPhysicalAddress}
                  testIdPrefix="territory-physical-address"
                />
              </Field>
              {legacyAddress && !physicalAddress.line1 && !physicalAddress.suburb && !physicalAddress.postcode && (
                <div
                  data-testid="territory-legacy-address"
                  className="mt-2 rounded-lg border border-slate-200 bg-slate-50 p-2.5 text-xs"
                >
                  <div className="flex items-start gap-1.5 text-slate-600">
                    <Home className="h-3.5 w-3.5 mt-0.5 flex-none" />
                    <div>
                      <b>Existing address on file:</b> {legacyAddress}
                      <div className="text-[11px] text-slate-400 mt-1">
                        Start typing above to replace this with a Google-verified structured address, or leave it as-is to keep the current text.
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <label className="mt-3 inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox" checked={mailOut}
                onChange={(e) => setMailOut(e.target.checked)}
                data-testid="territory-mail-out"
                className="h-4 w-4"
              />
              Mail-out kit capable (partner can ship posted kits)
            </label>
          </Section>

          {/* Audit trail */}
          {audit.length > 0 && (
            <Section title="Overlap audit trail" icon={Info}>
              <ul className="text-xs text-slate-600 space-y-2">
                {audit.slice(0, 5).map((a) => (
                  <li key={a.id} className="border-l-2 border-amber-300 pl-2.5">
                    <div className="text-slate-800">
                      {new Date(a.created_at).toLocaleString()} — <b>{a.admin_email || "(unknown admin)"}</b>
                    </div>
                    <div>Confirmed overlap on <span className="font-mono">{(a.proposed_postcodes || []).join(", ")}</span></div>
                    <div className="text-[11px] text-slate-500">
                      Conflicting: {(a.conflicts || []).map((c) => c.business_name).join(", ")}
                    </div>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          <div className="pt-2 flex items-center justify-end gap-2 border-t border-slate-100">
            <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-100">Cancel</button>
            <button
              onClick={save} disabled={!canSave}
              data-testid="territory-save"
              className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Save territory
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2 flex items-center gap-1.5">
        {Icon ? <Icon className="h-3.5 w-3.5" /> : null}{title}
      </div>
      {children}
    </div>
  );
}
function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs text-slate-600 font-medium mb-1">{label}</div>
      {children}
    </label>
  );
}
